import React, { useState } from "react";
import toast from "react-hot-toast";
import api from "../../../services/api";
import { FaPlus, FaTrash, FaBullseye, FaEdit, FaSave } from "react-icons/fa";

//https://claude.ai/public/artifacts/a32c8f1d-95a8-435a-8e45-639c78bc4254
  
export default function Prompts({ user, setUser }) {
  const [prompts, setPrompts] = useState(user?.prompts || [{ name: "", content: "" }]);
  const [selectedPromptIndex, setSelectedPromptIndex] = useState(0);
  const [goal, setGoal] = useState(user?.main_objective || "");
  const [isSaving, setIsSaving] = useState(false);

  function addPrompt() {
    const newPrompts = [...prompts, { name: "", content: "" }];
    setPrompts(newPrompts);
    setSelectedPromptIndex(newPrompts.length - 1);
  }

  function removePrompt(index) {
    const newPrompts = prompts.filter((_, i) => i !== index);
    setPrompts(newPrompts);
    if (selectedPromptIndex >= newPrompts.length) {
      setSelectedPromptIndex(Math.max(0, newPrompts.length - 1));
    }
  }

  function updatePrompt(field, value) {
    const newPrompts = [...prompts];
    newPrompts[selectedPromptIndex] = { ...newPrompts[selectedPromptIndex], [field]: value };
    setPrompts(newPrompts);
  }

  async function handleSaveUser() {
    const filteredPrompts = prompts.filter(prompt => 
      prompt.name.trim() !== "" || prompt.content.trim() !== ""
    );
    setIsSaving(true);
    try {
      const { ok } = await api.put(`/user/${user._id}`, { 
        prompts: filteredPrompts,
        main_objective: goal
      });
      if (!ok) return toast.error("Erreur lors de la sauvegarde");
      setUser({ ...user, prompts: filteredPrompts, main_objective: goal });
      toast.success("Prompts et objectif sauvegardés");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setIsSaving(false);
    }
  }

  const currentPrompt = prompts[selectedPromptIndex] || { name: "", content: "" };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Objectives & Prompts</h1>
          <p className="text-xs text-gray-600 mt-0.5">Manage user goals and AI prompts</p>
        </div>
        <button 
          onClick={handleSaveUser} 
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm transition-colors" 
          disabled={isSaving}
        >
          <FaSave size={12} />
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-md border border-gray-200 flex flex-col">
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-orange-100 rounded">
                <FaBullseye className="text-orange-600" size={16} />
              </div>
              <div>
                <h2 className="text-base font-medium text-gray-900">User Objectives</h2>
                <p className="text-xs text-gray-600">Define main goals and expected outcomes</p>
              </div>
            </div>
          </div>
          
          <div className="p-3 flex-1 flex flex-col">
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Main Objective
            </label>
            <textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md resize-none text-sm leading-normal focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
              placeholder="Describe the user's main goal or objective..."
              rows={18}
            />
            <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
              <span>{goal.length} characters</span>
              {goal.length > 500 && <span className="text-orange-600">Consider keeping it concise</span>}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-md border border-gray-200 flex flex-col">
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded">
                  <FaEdit className="text-blue-600" size={16} />
                </div>
                <div>
                  <h2 className="text-base font-medium text-gray-900">AI Prompts</h2>
                  <p className="text-xs text-gray-600">{prompts.length} prompt{prompts.length !== 1 ? 's' : ''} available</p>
                </div>
              </div>
              <button
                onClick={addPrompt}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs"
              >
                <FaPlus size={10} />
                Add
              </button>
            </div>
          </div>

          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Select Prompt to Edit
                </label>
                <select
                  value={selectedPromptIndex}
                  onChange={(e) => setSelectedPromptIndex(parseInt(e.target.value))}
                  className="w-full p-2.5 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {prompts.map((prompt, index) => (
                    <option key={index} value={index}>
                      {prompt.name || `Prompt ${index + 1}`}
                    </option>
                  ))}
                </select>
              </div>
              {prompts.length > 1 && (
                <button
                  onClick={() => removePrompt(selectedPromptIndex)}
                  className="mt-6 p-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200 transition-colors"
                  title="Remove selected prompt"
                >
                  <FaTrash size={12} />
                </button>
              )}
            </div>
          </div>

          <div className="p-3 flex-1 flex flex-col space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Prompt Name
              </label>
              <input
                type="text"
                value={currentPrompt.name}
                onChange={(e) => updatePrompt('name', e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter a descriptive name..."
              />
            </div>
            
            <div className="flex-1 flex flex-col">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Prompt Content
              </label>
              <textarea
                value={currentPrompt.content}
                onChange={(e) => updatePrompt('content', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md resize-none text-sm leading-normal focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Be specific about the task, context, and expected output format..."
                rows={12}
              />
              <div className="flex justify-between items-center mt-1.5 text-xs text-gray-500">
                <span>{currentPrompt.content.length} characters</span>
                {currentPrompt.content.length > 2000 && <span className="text-orange-600">Very long prompt - consider splitting</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}